/**
 * Shared Operator spreadsheet density for TabularGrid — hairline cells,
 * compact row height, focus ring, hover-only row/cell adornments.
 * Tokens only (theme.palette.*); no hex literals.
 */
import type { Theme } from '@mui/material/styles'
import { alpha } from '@mui/material/styles'
import { MONO_FONT } from '@/shared/typography'
import { tabularHeaderInk, tabularHeaderTypeSx, tabularToolbarBand } from '@/shared/form/formChrome'

/** Marker so tabular band type stays aligned with theme MuiTableHead. */
export const TABULAR_COL_HEADER_CLASS = 'tabular-col-header'

/** Target body-row height (px) for Variant A density. */
export const TABULAR_ROW_HEIGHT = 34

/** Floor so a flex-fill grid never collapses to a header-only strip. */
export const TABULAR_SCROLL_MIN_PX = 160

export function tabularContainerSx(theme: Theme) {
  return {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 1,
    overflow: 'hidden',
    bgcolor: 'background.paper',
    display: 'flex',
    flexDirection: 'column' as const,
    flex: 1,
    minHeight: 0,
  } as const
}

/**
 * Header + row scroller + column footer.
 * Vertical: rows scroll, chrome stays. Horizontal: all three move together
 * via `tabularHScrollSx` wrapping them.
 */
export const tabularGridPaneSx = {
  display: 'flex',
  flexDirection: 'column' as const,
  flex: 1,
  minHeight: TABULAR_SCROLL_MIN_PX,
  maxHeight: '50vh',
  minWidth: 0,
  overflow: 'hidden',
} as const

/** Shared left/right scroller for header, rows and column footer. */
export const tabularHScrollSx = {
  flex: 1,
  minHeight: 0,
  minWidth: 0,
  overflowX: 'auto' as const,
  overflowY: 'hidden' as const,
  display: 'flex',
  flexDirection: 'column' as const,
} as const

export const tabularHInnerSx = {
  display: 'flex',
  flexDirection: 'column' as const,
  flex: 1,
  minHeight: 0,
  height: '100%',
  width: '100%',
} as const

/** Vertical row scroller only — horizontal overflow lives on the parent. */
export const tabularScrollBodySx = {
  flex: 1,
  minHeight: 0,
  overflowX: 'hidden' as const,
  overflowY: 'auto' as const,
} as const

export function tabularTableSx(theme: Theme) {
  const hairline = `1px solid ${theme.palette.divider}`
  return {
    tableLayout: 'fixed' as const,
    borderCollapse: 'separate' as const,
    borderSpacing: 0,
    '& .MuiTableCell-root': {
      borderBottom: hairline,
      borderRight: hairline,
      py: 0,
      px: 0.25,
      height: TABULAR_ROW_HEIGHT,
      verticalAlign: 'middle',
      // Strip MUI field chrome so cells read as a spreadsheet.
      '& .MuiInputBase-root': {
        fontSize: 13,
        minHeight: TABULAR_ROW_HEIGHT - 2,
        '&:before, &:after': { display: 'none' },
      },
      '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
      '& .MuiInputBase-input': {
        py: '6px',
        px: 1,
        fontSize: 13,
      },
      '& .MuiAutocomplete-root': {
        width: '100%',
      },
      '& .MuiCheckbox-root': {
        p: 0.5,
      },
    },
    '& .MuiTableCell-root:last-of-type': {
      borderRight: 'none',
    },
    '& .MuiTableCell-footer': {
      bgcolor: tabularToolbarBand(theme),
      color: tabularHeaderInk(theme),
      fontWeight: 700,
      fontSize: 13,
      borderTop: `1px solid ${theme.palette.divider}`,
      height: 'auto',
      py: 0.5,
    },
    [`& .MuiTableHead-root .MuiTableCell-head.${TABULAR_COL_HEADER_CLASS}`]: {
      bgcolor: tabularToolbarBand(theme),
      ...tabularHeaderTypeSx(theme),
      borderBottom: `1px solid ${theme.palette.divider}`,
      height: 32,
      py: 0.25,
    },
    '& .MuiTableCell-root:focus-within': {
      position: 'relative',
      outline: `1px solid ${theme.palette.primary.main}`,
      outlineOffset: -1,
      bgcolor: alpha(theme.palette.primary.main, 0.04),
      zIndex: 1,
    },
    // Row delete — visible on row hover / focus-within only.
    '& .tabular-row-action': {
      opacity: 0,
      transition: 'opacity 120ms ease',
    },
    '& .MuiTableRow-root:hover .tabular-row-action, & .MuiTableRow-root:focus-within .tabular-row-action':
      {
        opacity: 1,
      },
    // Per-cell adornments (calc, autocomplete popup, open-ref).
    '& .tabular-cell-adornment': {
      opacity: 0,
      transition: 'opacity 120ms ease',
    },
    '& .MuiTableCell-root:hover .tabular-cell-adornment, & .MuiTableCell-root:focus-within .tabular-cell-adornment':
      {
        opacity: 1,
      },
  }
}

export const tabularMonoInputSx = {
  fontFamily: MONO_FONT,
  textAlign: 'right' as const,
  fontVariantNumeric: 'tabular-nums',
}
